import React from 'react';
import { NavigationScreen } from '@/types';
import {
  LayoutDashboard,
  Video,
  Mic,
  Users,
  Share2,
  FolderGit2,
  Settings,
  Scissors,
  ShieldCheck,
  HardDrive,
  Sun,
  Moon,
  Sparkles,
  HelpCircle,
  Globe,
  CalendarDays
} from 'lucide-react';
import { formatFileSize } from '@/utils/helpers';

interface SidebarProps {
  currentScreen: NavigationScreen;
  onNavigate: (screen: NavigationScreen) => void;
  isRecordingActive: boolean;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  storageMetrics: { totalBytes: number; totalRecordings: number };
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentScreen,
  onNavigate,
  isRecordingActive,
  theme,
  onToggleTheme,
  storageMetrics
}) => {
  const navItems = [
    { id: 'dashboard' as NavigationScreen, label: 'Dashboard', icon: LayoutDashboard },
    {
      id: isRecordingActive ? ('active-recording' as NavigationScreen) : ('studio' as NavigationScreen),
      label: isRecordingActive ? 'Active Studio' : 'Recording Studio',
      icon: Video,
      badge: isRecordingActive ? 'REC' : undefined,
      isRecordingPill: isRecordingActive
    },
    { id: 'podcast-studio' as NavigationScreen, label: 'Podcast Studio', icon: Mic },
    { id: 'group-meeting' as NavigationScreen, label: 'Group Meeting', icon: Globe },
    { id: 'scheduler' as NavigationScreen, label: 'Scheduler', icon: CalendarDays },
    { id: 'separate-videos' as NavigationScreen, label: 'Separate Videos', icon: Users },
    { id: 'participant-review' as NavigationScreen, label: 'Video Editor', icon: Scissors },
    { id: 'export-center' as NavigationScreen, label: 'Export Center', icon: Share2 },
    { id: 'library' as NavigationScreen, label: 'Recording Library', icon: FolderGit2 },
    { id: 'settings' as NavigationScreen, label: 'Settings', icon: Settings },
  ];

  return (
    <aside className="w-64 bg-brand-sidebar border-r border-brand-border flex flex-col justify-between shrink-0 select-none transition-colors duration-200">
      {/* Top Branding Section */}
      <div>
        <div className="p-5 border-b border-brand-border/60">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => onNavigate('dashboard')}>
            <div className="w-10 h-10 rounded-xl overflow-hidden shadow-glow ring-1 ring-brand-border shrink-0 bg-brand-surface flex items-center justify-center">
              <img
                src="./app-icon.png"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent && !parent.querySelector('.fallback-logo')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'fallback-logo w-full h-full bg-gradient-to-tr from-purple-600 to-indigo-500 flex items-center justify-center text-white font-black text-sm select-none';
                    fallback.innerText = 'M';
                    parent.appendChild(fallback);
                  }
                }}
                alt="MeetSplit AI Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="font-bold text-base tracking-tight text-brand-text flex items-center gap-1.5">
                MeetSplit <span className="text-xs px-1.5 py-0.5 rounded bg-brand-accent/20 text-brand-accent font-mono font-semibold">AI</span>
              </h1>
              <p className="text-[10px] text-brand-muted tracking-tight">Record Once. Split Every Speaker.</p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="p-3 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentScreen === item.id || (item.id === 'studio' && currentScreen === 'active-recording');

            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-brand-surface text-brand-text border border-brand-border shadow-sm font-semibold'
                    : 'text-brand-muted hover:text-brand-text hover:bg-brand-surface/40'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-4 h-4 ${isActive ? 'text-brand-accent' : 'text-brand-muted'}`} />
                  <span>{item.label}</span>
                </div>

                {item.badge && (
                  <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-brand-recording text-white animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-record-blink" />
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Bottom Status & Diagnostics */}
      <div className="p-4 space-y-3 border-t border-brand-border/60">
        {/* Local Storage Card */}
        <div className="p-3 rounded-xl bg-brand-surface/60 border border-brand-border/40 text-xs">
          <div className="flex items-center justify-between text-brand-muted mb-1.5">
            <span className="flex items-center gap-1.5">
              <HardDrive className="w-3.5 h-3.5 text-brand-secondary" />
              Local Storage
            </span>
            <span className="font-mono text-[11px] text-brand-text">
              {formatFileSize(storageMetrics.totalBytes)}
            </span>
          </div>
          <div className="w-full bg-brand-elevated h-1.5 rounded-full overflow-hidden">
            <div
              className="bg-brand-secondary h-full rounded-full"
              style={{ width: `${Math.min(100, Math.max(5, (storageMetrics.totalBytes / (1024 * 1024 * 1024 * 20)) * 100))}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-brand-muted mt-1">
            <span>{storageMetrics.totalRecordings} recordings</span>
            <span>Local Disk</span>
          </div>
        </div>

        {/* Privacy Shield */}
        <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-brand-elevated/40 border border-brand-border/30 text-xs text-brand-muted">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <span className="text-[11px]">100% Local Processing</span>
          </div>
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        </div>

        {/* Theme and Help toggles */}
        <div className="flex items-center justify-between pt-1">
          <button
            onClick={() => onNavigate('onboarding')}
            className="flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-text transition-colors"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Setup Guide</span>
          </button>

          <button
            onClick={onToggleTheme}
            className="p-1.5 rounded-lg bg-brand-surface border border-brand-border text-brand-muted hover:text-brand-text transition-colors"
            title="Toggle Dark/Light Mode"
          >
            {theme === 'dark' ? <Sun className="w-3.5 h-3.5 text-amber-300" /> : <Moon className="w-3.5 h-3.5 text-brand-accent" />}
          </button>
        </div>
      </div>
    </aside>
  );
};
